import { useMemo } from 'react';

import { useDirectoryGetSchemas } from 'api/requests';
import { OrderSchema } from 'api/types/data';

import {
  CurrenciesSchema,
  FinancialInstrumentsSchema,
  SenderOrRecipientDetailsSchema,
} from '../../common/types';

interface Props {
  orderSchema?: OrderSchema;
}

export const useDirectories = ({ orderSchema }: Props) => {
  const { schemas, loading: loadingSchema } = useDirectoryGetSchemas();

  const directories = useMemo(() => {
    if (!orderSchema || !schemas) {
      return {
        financialInstruments: {} as FinancialInstrumentsSchema,
        currencies: {} as CurrenciesSchema,
        senderOrRecipientDetails: {} as SenderOrRecipientDetailsSchema,
      };
    }

    const financialInstruments: FinancialInstrumentsSchema = {
      isin: {
        directorySchemaName:
          orderSchema.financialInstrument.isin.directorySchemaName,
        fieldName: orderSchema.financialInstrument.isin.fieldName,
      },
      securityType: {
        directorySchemaName:
          orderSchema.financialInstrument.securityType.directorySchemaName,
        fieldName: orderSchema.financialInstrument.securityType.fieldName,
      },
      issuer: {
        directorySchemaName:
          orderSchema.financialInstrument.issuer.directorySchemaName,
        fieldName: orderSchema.financialInstrument.issuer.fieldName,
      },
      nominalValue: {
        directorySchemaName:
          orderSchema.transactionVolume.nominalQuantity.directorySchemaName,
        fieldName: orderSchema.transactionVolume.nominalQuantity.fieldName,
      },
    };

    const currencies: CurrenciesSchema = {
      currency: {
        directorySchemaName: orderSchema.currency.directorySchemaName,
        fieldName: orderSchema.currency.fieldName,
      },
    };

    const senderOrRecipientDetails: SenderOrRecipientDetailsSchema = {
      custodianOrganization: {
        directorySchemaName:
          orderSchema.senderDetails.custodianOrganization.directorySchemaName,
        fieldName: orderSchema.senderDetails.custodianOrganization.fieldName,
        directoryRecordFieldFilters:
          orderSchema.senderDetails.custodianOrganization
            .directoryRecordFieldFilters,
      },
      brokerName: {
        directorySchemaName:
          orderSchema.senderDetails.brokerName.directorySchemaName,
        fieldName: orderSchema.senderDetails.brokerName.fieldName,
      },
    };

    return { financialInstruments, currencies, senderOrRecipientDetails };
  }, [orderSchema, schemas]);

  const loading = !schemas && loadingSchema;

  return { loading, directories, schemas };
};
